const TICKET_TIMEZONE = 'Asia/Tokyo';
const TIME_PARSE_RE = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/;

const tzFormatterCache = new Map<string, Intl.DateTimeFormat>();
const getTzFormatter = (timeZone: string) => {
  if (!tzFormatterCache.has(timeZone)) {
    tzFormatterCache.set(
      timeZone,
      new Intl.DateTimeFormat('en-US', {
        timeZone,
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
        hour12: false
      })
    );
  }
  return tzFormatterCache.get(timeZone)!;
};

/**
 * Today's calendar day in Japan, where every ticket date lives, as a local
 * midnight Date so getFullYear/getMonth/getDate read the Japan day. The
 * browser's own date is a day off for visitors abroad around midnight JST.
 */
export const getJapanToday = (): Date => {
  const parts = getTzFormatter(TICKET_TIMEZONE).formatToParts(new Date());
  const get = (type: string) =>
    Number(parts.find(part => part.type === type)?.value);
  return new Date(get('year'), get('month') - 1, get('day'));
};

/**
 * Parses a local time string as if it were in the given IANA timezone, returning a UTC Date.
 * Works correctly regardless of the browser's local timezone.
 */
export const parseInTimezone = (localStr: string, timeZone: string): Date => {
  const normalized = localStr.replace(/\//g, '-').replace(' ', 'T');
  const approx = new Date(`${normalized}Z`);

  const partsMap = new Map<string, number>(
    getTzFormatter(timeZone)
      .formatToParts(approx)
      .map(p => [p.type, Number(p.value)])
  );
  const get = (type: string) => partsMap.get(type)!;

  const tzEquivalent = new Date(
    Date.UTC(
      get('year'),
      get('month') - 1,
      get('day'),
      get('hour') % 24,
      get('minute'),
      get('second')
    )
  );

  const offset = approx.getTime() - tzEquivalent.getTime();
  return new Date(approx.getTime() + offset);
};

/**
 * Extracts the first time value from a string. Handles single times ("09:00"),
 * ranges with any separator ("13:00 - 13:29", "10:00 ～ 11:00"), and
 * trailing-separator formats ("10:00 ～").
 */
export const extractStartTime = (time: string): string => {
  if (!time) return '';
  return time.match(/\d{1,2}:\d{2}(?::\d{2})?/)?.[0] ?? '';
};

/**
 * Normalizes any time-ish string ("9:00", "13:00:00", "10:00 ～ 11:00",
 * datetime) to a zero-padded HH:mm value, or '' when no time can be extracted.
 * Time strings must be normalized through this before being compared with
 * < / > — mixed formats ("9:00" vs "09:00", "13:00:00" vs "13:00") do not
 * order correctly as raw strings.
 */
export const normalizeTimeValue = (time: string): string =>
  formatTime(extractStartTime(time));

/**
 * Logical start-time value for time comparisons and window math. Only the
 * canonical `time` field is valid for comparisons; `title` is display text and
 * must never be parsed as a fallback.
 */
export const resolveStartTimeValue = (startTime?: { time?: string }): string =>
  normalizeTimeValue(startTime?.time ?? '');

/**
 * Looks up a start time by id and resolves its logical value. An empty id
 * means "nothing selected" and never matches a slot, even one with a blank id.
 */
export const resolveStartTimeById = (
  startTimes: { id?: string; time?: string }[] | undefined,
  startTimeId: string | undefined
): string =>
  startTimeId
    ? resolveStartTimeValue(startTimes?.find(st => st.id === startTimeId))
    : '';

/**
 * Returns true if the activity start time (in Japan timezone) is strictly in the past.
 * @param targetDate - e.g. "2024-06-01"
 * @param planStartTime - e.g. "09:00", "09:00:00", or "13:00 ～ 13:29" (range — start is used)
 */
export const isStartTimeExpired = (
  targetDate: string,
  planStartTime: string
): boolean => {
  if (!targetDate || !planStartTime) return false;
  const rawTime = extractStartTime(planStartTime);
  if (!rawTime) return false;
  try {
    return (
      parseInTimezone(`${targetDate} ${rawTime}`, TICKET_TIMEZONE).getTime() <
      Date.now()
    );
  } catch {
    return false;
  }
};

export function parseDate(date: string): Date {
  const [year, month, day] = date.split('-');
  return new Date(Number(year), Number(month) - 1, Number(day));
}
export const validDate = (startDate: string, endDate: string) => {
  const start = parseDate(startDate);
  const end = parseDate(endDate);
  return start.getTime() <= end.getTime();
};

export const today = (format: string = 'YYYY-MM-DD'): string => {
  return formatDateTime(new Date(), format);
};

/**
 * Format date and time with custom format string
 * @param date - The date object to format
 * @param format - Format string with the following placeholders:
 *   - YYYY: 4-digit year
 *   - MM: 2-digit month (01-12)
 *   - DD: 2-digit day (01-31)
 *   - HH: 2-digit hour (00-23)
 *   - mm: 2-digit minute (00-59)
 *   - ss: 2-digit second (00-59)
 * @returns Formatted date and time string
 * @example
 * formatDateTime(new Date(), 'YYYY-MM-DD HH:mm:ss') // '2025-11-13 14:30:45'
 * formatDateTime(new Date(), 'YYYY-MM-DD HH:mm') // '2025-11-13 14:30'
 * formatDateTime(new Date(), 'YYYY-MM-DD') // '2025-11-13'
 */
export const formatDateTime = (date: Date, format: string): string => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const seconds = date.getSeconds().toString().padStart(2, '0');

  return format
    .replace('YYYY', year.toString())
    .replace('MM', month)
    .replace('DD', day)
    .replace('HH', hours)
    .replace('mm', minutes)
    .replace('ss', seconds);
};

/**
 * Format time string with custom format
 * @param time - The time string to format. Can be:
 *   - Pure time string: "14:30:45", "14:30"
 *   - Full datetime string: "2025-01-19T14:30:45", "2025-01-19 14:30:45"
 *   - ISO timestamp: "2025-01-19T14:30:45.000Z"
 * @param format - Format string (default: 'HH:mm'). Only supports time placeholders:
 *   - HH: 2-digit hour (00-23)
 *   - mm: 2-digit minute (00-59)
 *   - ss: 2-digit second (00-59)
 * @returns Formatted time string (without date)
 * @example
 * formatTime('14:30:45') // '14:30'
 * formatTime('14:30') // '14:30'
 * formatTime('2025-01-19T14:30:45') // '14:30'
 * formatTime('14:30:45', 'HH:mm:ss') // '14:30:45'
 */
export const formatTime = (time: string, format: string = 'HH:mm'): string => {
  // Check if it's a pure time string (HH:mm or HH:mm:ss)
  if (!time) return '';
  const timeOnlyRegex = TIME_PARSE_RE;
  const match = time.match(timeOnlyRegex);

  let hours: string;
  let minutes: string;
  let seconds: string;

  if (match) {
    hours = match[1].padStart(2, '0');
    minutes = match[2];
    seconds = match[3] || '00';
  } else {
    const date = new Date(time);
    if (isNaN(date.getTime())) {
      return '';
    }
    hours = date.getHours().toString().padStart(2, '0');
    minutes = date.getMinutes().toString().padStart(2, '0');
    seconds = date.getSeconds().toString().padStart(2, '0');
  }

  return format
    .replace('HH', hours)
    .replace('mm', minutes)
    .replace('ss', seconds);
};

/**
 * Add minutes to a time string. Preserves the input's format (HH:mm or HH:mm:ss).
 * Returns the original string when it does not match a recognized time pattern.
 * Hours overflow naturally past 24 so the result still sorts string-wise.
 */
export const addMinutesToTime = (time: string, minutes: number): string => {
  if (!time || !minutes) return time;
  const match = time.match(TIME_PARSE_RE);
  if (!match) return time;
  const h = parseInt(match[1], 10);
  const m = parseInt(match[2], 10);
  const totalMinutes = h * 60 + m + minutes;
  const newH = Math.floor(totalMinutes / 60);
  const newM = ((totalMinutes % 60) + 60) % 60;
  const hh = newH.toString().padStart(2, '0');
  const mm = newM.toString().padStart(2, '0');
  return match[3] ? `${hh}:${mm}:${match[3]}` : `${hh}:${mm}`;
};
